import React, { useEffect, useState } from 'react';
import { BASE_URL } from '../config/api'; // Assuming BASE_URL is correctly defined
import { IMG_URL } from '../config/api'; // Assuming BASE_URL is correctly defined
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf'; // Import jsPDF
import 'jspdf-autotable'; // Import for table support
import * as XLSX from 'xlsx'; // Import xlsx for Excel export

const VPList = () => {
    const [vps, setVps] = useState([]);
    const [filteredVps, setFilteredVps] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [selectedVpId, setSelectedVpId] = useState(null); // Store the selected VP ID
    const [statusModalVisible, setStatusModalVisible] = useState(false); // Control modal visibility
    const [newStatus, setNewStatus] = useState(''); // Store selected status
    const navigate = useNavigate();

    // Fetch VP data when the component mounts
    useEffect(() => {
        fetch(`${BASE_URL}vp/`)
            .then(response => response.json())
            .then(data => {
                if (data.status === 1) {
                    setVps(data.data); // Store the VPs data
                    setFilteredVps(data.data); // Set initial filtered VPs
                } else {
                    alert('Error fetching VPs');
                }
            })
            .catch(error => console.error('Error fetching VPs:', error));
    }, []);

    // Handle search term change
    const handleSearchChange = (event) => {
        const term = event.target.value;
        setSearchTerm(term);
        filterVps(term, startDate, endDate);
    };

    // Handle date range change
    const handleDateChange = (start, end) => {
        setStartDate(start);
        setEndDate(end);
    };

    // Filter VPs based on search term, start date, and end date
    const filterVps = (term, start, end) => {
        const filtered = vps.filter(vp => {
            const matchesSearch = vp.name.toLowerCase().includes(term.toLowerCase());

            // Convert the lead's created_at date to a Date object for comparison
            const vpDate = new Date(vp.created_at);
            const formattedVpDate = new Date(vpDate.setHours(0, 0, 0, 0));

            const formattedStartDate = start ? new Date(new Date(start).setHours(0, 0, 0, 0)) : null;
            const formattedEndDate = end ? new Date(new Date(end).setHours(0, 0, 0, 0)) : null;

            const matchesDate =
                (!formattedStartDate || formattedVpDate >= formattedStartDate) &&
                (!formattedEndDate || formattedVpDate <= formattedEndDate);

            return matchesSearch && matchesDate;
        });

        setFilteredVps(filtered);
    };

    // Navigate to the edit VP page
    const handleEdit = (id) => {
        navigate(`/EditVP/${id}`);
    };

    // Handle VP status change - Open the modal
    const handleStatusChange = (id) => {
        setSelectedVpId(id);
        setStatusModalVisible(true);
    };

    // Handle status update on button click
    const updateStatus = (status) => {
        fetch(`${BASE_URL}vp/${selectedVpId}/status`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ status }), // Send the selected status
        })
            .then(response => response.json())
            .then(data => {
                if (data.status === 1) {
                    // Update status in the local state
                    setVps(vps.map(vp => vp.id === selectedVpId ? { ...vp, status } : vp));
                    setFilteredVps(filteredVps.map(vp => vp.id === selectedVpId ? { ...vp, status } : vp));
                    setStatusModalVisible(false); // Close the modal
                } else {
                    alert('Failed to update VP status');
                }
            })
            .catch(error => {
                console.error('Error updating VP status:', error);
                alert('Error updating VP status');
            });
    };

    // Export filtered VPs to PDF
    const handleExportPDF = () => {
        const doc = new jsPDF();

        // Add Title
        doc.text('VP List', 14, 10); // Title at top of PDF

        // Prepare table data for PDF
        const tableData = filteredVps.map(vp => [
            vp.id,
            vp.name || 'N/A',
            vp.amount || 'N/A',
            vp.status || 'N/A',
            new Date(vp.created_at).toLocaleString(),
        ]);

        // Add table to PDF with autoTable
        doc.autoTable({
            head: [['ID', 'Name', 'Amount', 'Status', 'Created At']], // Table headers
            body: tableData,
        });

        // Save the PDF with a filename
        doc.save('vp_list.pdf');
    };

    // Export filtered VPs to Excel
    const handleExportExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(filteredVps.map(vp => ({
            ID: vp.id,
            Name: vp.name || 'N/A',
            Amount: vp.amount || 'N/A',
            Status: vp.status || 'N/A',
            CreatedAt: new Date(vp.created_at).toLocaleString(),
        })));

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "VP List");

        // Save the Excel file with a filename
        XLSX.writeFile(workbook, 'vp_list.xlsx');
    };

    return (
        <div id="main">
            <header className="mb-3">
                <a href="#" className="burger-btn d-block d-xl-none">
                    <i className="bi bi-justify fs-3"></i>
                </a>
            </header>

            <div className="page-heading">
                <div className="page-title">
                    <div className="row">
                        <div className="col-12 col-md-6 order-md-1 order-last">
                            <h3>VP List</h3>
                        </div>
                        <div className="col-12 col-md-6 order-md-2 order-first">
                            <nav aria-label="breadcrumb" className="breadcrumb-header float-start float-lg-end">
                                <ol className="breadcrumb">
                                </ol>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>

            {/* VP Table */}
            <section className="section">
                <div className="row" id="basic-table">
                    <div className="col-12 col-md-12">
                        <div className="card">
                            <div className="card-header">
                                <div className="row">
                                    <div className="col-md-6">
                                    </div>
                                    <div className="col-md-6 text-end">
                                        <button className="btn btn-primary" onClick={() => navigate('/AddVP')}>Add Vpp</button>
                                        <button className="btn btn-secondary ms-2" onClick={handleExportPDF}>Export to PDF</button>
                                    </div>
                                </div>
                            </div>
                            <div className="card-body">
                                {/* Search and Date Filters */}
                               
                                <div className="row mb-3">
                                     <div className="col-md-4">
                                    <label htmlFor="searchTerm" className="form-label">Search by Name</label>
                                    <input
                                        type="text"
                                        id="searchTerm"
                                        className="form-control"
                                        placeholder="Search VP name"
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                    />
                                </div>
                                    <div className="col-md-4">
                                        <label htmlFor="startDate" className="form-label">Start Date</label>
                                        <input
                                            type="date"
                                            id="startDate"
                                            className="form-control"
                                            value={startDate}
                                            onChange={(e) => handleDateChange(e.target.value, endDate)}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label htmlFor="endDate" className="form-label">End Date</label>
                                        <input
                                            type="date"
                                            id="endDate"
                                            className="form-control"
                                            value={endDate}
                                            onChange={(e) => handleDateChange(startDate, e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <button className="btn btn-secondary" onClick={() => filterVps(searchTerm, startDate, endDate)}>Apply Date Filter</button>
                                </div>

                                <div className="table-responsive">
                                    <table className="table table-lg">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Name</th>
                                                <th>Angadia Name</th>
                                                <th>Image</th>
                                                <th>Amount</th>
                                                <th>Status</th>
                                                <th className="text-end">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredVps.map(vp => (
                                                <tr key={vp.id}>
                                                    <td>{vp.id}</td>
                                                    <td>{vp.name || 'N/A'}</td>
                                                    <td>{vp.aname || 'N/A'}</td>
                                                    <td>
                                                        <img 
                                                            src={`${IMG_URL}uploads/vp_images/${vp.image}`} 
                                                            alt={vp.name} 
                                                            style={{ width: '50px', height: '50px', objectFit: 'cover' }} 
                                                        />
                                                    </td>
                                                    <td>{vp.amount || 'N/A'}</td>
                                                    <td>{vp.status || 'N/A'}</td>
                                                    <td className="text-end">
                                                        <button className="btn btn-success" onClick={() => handleEdit(vp.id)}>Edit</button>
                                                        <button className="btn btn-warning ms-2" onClick={() => handleStatusChange(vp.id)}>Status</button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Status Modal */}
            {statusModalVisible && (
                <div className="modal" style={{ display: 'block' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Change Status</h5>
                                <button type="button" className="close" onClick={() => setStatusModalVisible(false)}>&times;</button>
                            </div>
                            <div className="modal-body">
                                <p>Choose a status:</p>
                                <button className="btn btn-success ms-2" onClick={() => updateStatus('completed')}>Completed</button>
                                <button className="btn btn-warning ms-2" onClick={() => updateStatus('pending')}>Pending</button>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setStatusModalVisible(false)}>Cancel</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VPList;
